import { z } from 'zod';

import { type PurlSignUpPayload } from '@/modules/api';
import {
  deriveSignedDocuments,
  isLegalKeyAgreed,
  type LegalDocumentGroup,
} from '@/modules/legalDocuments';
import { getServerLocale, getSystemLocale } from '@/modules/localization';

import { LEGAL_KEY } from '../constants';
import { accountSchema } from '../schema/accountSchema';

type FormValues = z.infer<typeof accountSchema>;

// Assembles the PURL sign-up payload from the account-creation form values
// + the live legal-document catalog. Unlike buildCreateUserPayload, the DOB isn't
// a form field here — it's the `YYYY-MM-DD` value already confirmed on the
// dedicated DOB entry screen (verify-dob) — and the token comes from the
// validated PURL state. Identity (first/last name, phone) is omitted: the backend
// has it from the b2b eligibility record the token resolves to.
export const buildPurlSignUpPayload = (
  values: FormValues,
  legalGroups: LegalDocumentGroup[],
  token: string,
  verifiedDob: string
): PurlSignUpPayload => {
  const [dobYear, dobMonth, dobDay] = verifiedDob.split('-');
  // Trim once, then send the trimmed value — never leak the surrounding
  // whitespace the guard tested against.
  const phone = values.phone?.trim();
  return {
    token,
    dobYear,
    dobMonth,
    dobDay,
    email: values.email,
    password: values.password,
    // Optional — omit when the field is left blank.
    ...(phone ? { phone } : {}),
    termsAndConditionsAccepted: isLegalKeyAgreed(values.agreements, legalGroups, LEGAL_KEY.TERMS),
    informedConsentAccepted: isLegalKeyAgreed(values.agreements, legalGroups, LEGAL_KEY.CONSENT),
    smsAllowed: values.sms ?? false,
    preferredLanguage: getServerLocale(getSystemLocale()),
    signedDocuments: deriveSignedDocuments(values.agreements, legalGroups),
  };
};
